import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// ant design icons
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';

// project imports
import MainCard from 'components/MainCard';
import HistoryPagination, { HistoryEmptyState } from 'components/HistoryPagination';
import useDatasheetHistory, { HISTORY_PAGE_SIZE, historyDateKey } from 'hooks/useDatasheetHistory';

const ToiletingProgramMode = ({
  onDataChange,
  viewMode = 'individual',
  childId,
  childName = '',
  therapistId,
  therapistName = '',
  sessionId = null
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Prompt levels - Prompted/Unprompted/Self-Initiated
  const PROMPT_LEVELS = [
    { value: 'P', label: 'P', description: intl.formatMessage({ id: 'form.toileting.prompted' }) },
    { value: 'U', label: 'U', description: intl.formatMessage({ id: 'form.toileting.unprompted' }) },
    { value: 'SI', label: 'SI', description: intl.formatMessage({ id: 'form.toileting.selfInitiated' }) }
  ];

  // Internal form mode: 'list' or 'form' - used when in grid view to toggle between list and form
  const [internalFormMode, setInternalFormMode] = useState('list');

  // Tab value for grid view - 0: Data Entry, 1: Historical Data
  const [tabValue, setTabValue] = useState(0);

  // Form visibility state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Records entered in this sitting.
  const [records, setRecords] = useState([]);

  /* Previously submitted records. The Historical tab used to render `records`, which
   * starts empty and is only appended to as the therapist works — it could never
   * show a previous day. */
  const {
    records: historyRecords,
    page: historyPage,
    totalPages: historyTotalPages,
    total: historyTotal,
    setPage: setHistoryPage,
    loading: historyLoading,
    isEmpty: historyIsEmpty
  } = useDatasheetHistory(childId, 'toileting_program');

  const savedRecords = useMemo(
    () =>
      historyRecords.flatMap((record) => {
        const key = historyDateKey(record);
        const rows = record.raw_data?.records;
        if (!key || !Array.isArray(rows)) return [];
        const [y, m, d] = key.split('-').map(Number);
        if (!y || !m || !d) return [];
        const day = new Date(y, m - 1, d);
        // A stored row carries its own date; fall back to the entry's day.
        return rows.map((r, i) => ({ ...r, id: r.id || `saved-${record.id}-${i}`, date: r.date ? new Date(r.date) : day }));
      }),
    [historyRecords]
  );

  /* This sitting's rows plus the saved ones, ids deduped — after a save the fetch
   * returns rows still held in `records` too. */
  const allRecords = useMemo(() => {
    const seen = new Set(records.map((r) => r.id));
    return [...records, ...savedRecords.filter((r) => !seen.has(r.id))];
  }, [records, savedRecords]);

  // Helper function to check if a date is today
  const isToday = (dateValue) => {
    if (!dateValue) return false;
    const today = new Date();
    const checkDate = new Date(dateValue);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // New record form state
  const [newRecord, setNewRecord] = useState({
    date: new Date(),
    time: new Date(),
    toiletVoid: null,
    accident: null,
    prompt: '',
    comments: ''
  });

  // Inline editing state for grid view (Type 1)
  const [inlineEditing, setInlineEditing] = useState(false);
  const [inlineDate, setInlineDate] = useState(new Date());
  const [inlineTime, setInlineTime] = useState(new Date());
  const [inlineToiletVoid, setInlineToiletVoid] = useState(null);
  const [inlineAccident, setInlineAccident] = useState(null);
  const [inlinePrompt, setInlinePrompt] = useState('');
  const [inlineComments, setInlineComments] = useState('');

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Auto-save: notify parent when form data changes so it can debounce and persist
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no meaningful data has been entered
    if (records.length === 0) return;

    const formData = {
      records,
      newRecord
    };

    if (onDataChange) onDataChange('toileting-program', formData);
  }, [records, newRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter records for today (Data Entry tab) - Type 1
  const todaysRecords = useMemo(() => {
    return records.filter((record) => isToday(record.date));
  }, [records]);

  // Handle new record in grid view - use inline editing
  const handleNewRecord = () => {
    // Reset inline editing state
    setInlineDate(new Date());
    setInlineTime(new Date());
    setInlineToiletVoid(null);
    setInlineAccident(null);
    setInlinePrompt('');
    setInlineComments('');
    setInlineEditing(true);
  };

  // Handle editing existing record in grid view
  const handleEditRecordGridView = (record) => {
    setEditingRecord(record);
    setNewRecord({
      date: record.date,
      time: record.time,
      toiletVoid: record.toiletVoid,
      accident: record.accident,
      prompt: record.prompt,
      comments: record.comments
    });
    setInternalFormMode('form');
  };

  // Go back to list view
  const handleBackToList = () => {
    setNewRecord({
      date: new Date(),
      time: new Date(),
      toiletVoid: null,
      accident: null,
      prompt: '',
      comments: ''
    });
    setEditingRecord(null);
    setInternalFormMode('list');
  };

  // Handle delete confirmation
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (recordToDelete) {
      setRecords((prev) => prev.filter((r) => r.id !== recordToDelete.id));
      onDataChange && onDataChange();
    }
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  // Save and go back to list (for grid view form)
  const handleSaveAndBackToList = () => {
    handleSaveRecord();
    setInternalFormMode('list');
  };

  // Save inline record (Type 1)
  const handleSaveInlineRecord = () => {
    if (!inlineToiletVoid && !inlineAccident && !inlinePrompt) {
      return; // Need at least some data
    }

    const newRec = {
      id: `record-${Date.now()}`,
      date: inlineDate,
      time: inlineTime,
      toiletVoid: inlineToiletVoid,
      accident: inlineAccident,
      prompt: inlinePrompt,
      comments: inlineComments
    };

    setRecords((prevRecords) => {
      const updated = [newRec, ...prevRecords];
      return updated.sort((a, b) => {
        const timeA = a.time ? new Date(a.time).getTime() : 0;
        const timeB = b.time ? new Date(b.time).getTime() : 0;
        return timeB - timeA;
      });
    });

    // Reset inline editing
    setInlineEditing(false);
    setInlineDate(new Date());
    setInlineTime(new Date());
    setInlineToiletVoid(null);
    setInlineAccident(null);
    setInlinePrompt('');
    setInlineComments('');

    if (onDataChange) onDataChange();
  };

  // Cancel inline editing (Type 1)
  const handleCancelInline = () => {
    setInlineEditing(false);
    setInlineDate(new Date());
    setInlineTime(new Date());
    setInlineToiletVoid(null);
    setInlineAccident(null);
    setInlinePrompt('');
    setInlineComments('');
  };

  // Sort helper function
  const sortByTime = (recordsArray) => {
    return recordsArray.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA;
    });
  };

  // Handle add/update record
  // Does NOT call saveDataEntry directly — the useEffect auto-save via onDataChange
  // handles persistence to IndexedDB, avoiding duplicate entries.
  const handleSaveRecord = () => {
    const record = {
      id: editingRecord?.id || `record-${Date.now()}`,
      ...newRecord
    };

    if (editingRecord) {
      // Update existing record
      setRecords((prevRecords) => {
        const updated = prevRecords.map((r) => (r.id === editingRecord.id ? record : r));
        return sortByTime(updated);
      });
      setEditingRecord(null);
    } else {
      // Add new record
      setRecords((prevRecords) => sortByTime([record, ...prevRecords]));
    }

    // Reset form
    setNewRecord({
      date: new Date(),
      time: new Date(),
      toiletVoid: null,
      accident: null,
      prompt: '',
      comments: ''
    });
    setShowAddForm(false);
  };

  // Handle edit record
  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setNewRecord({
      date: record.date,
      time: record.time,
      toiletVoid: record.toiletVoid,
      accident: record.accident,
      prompt: record.prompt,
      comments: record.comments
    });
    setShowAddForm(true);
  };

  // Handle delete record
  const handleDeleteRecord = (id) => {
    setRecords(records.filter((r) => r.id !== id));
    if (onDataChange) onDataChange();
  };

  // Handle cancel form
  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingRecord(null);
    setNewRecord({
      date: new Date(),
      time: new Date(),
      toiletVoid: null,
      accident: null,
      prompt: '',
      comments: ''
    });
  };

  // Handle inline record change (for grid view)
  const handleRecordChange = (id, field, value) => {
    setRecords((prevRecords) => {
      const updated = prevRecords.map((r) => (r.id === id ? { ...r, [field]: value } : r));
      return sortByTime(updated);
    });
    if (onDataChange) onDataChange();
  };

  // Add new record for grid view — reserved for an upcoming "+ Add" button
  // that hasn't been wired into the UI yet.
  // eslint-disable-next-line no-unused-vars
  const handleAddNewRecord = () => {
    const record = {
      id: `record-${Date.now()}`,
      date: new Date(),
      time: new Date(),
      toiletVoid: null,
      accident: null,
      prompt: '',
      comments: ''
    };
    setRecords((prevRecords) => sortByTime([record, ...prevRecords]));
    if (onDataChange) onDataChange();
  };

  // Get void display info
  const getVoidInfo = (value) => {
    switch (value) {
      case 'BM':
        return { label: 'BM', color: 'success', description: 'Bowel Movement' };
      case 'U':
        return { label: 'Urine', color: 'info', description: 'Urine' };
      case 'N':
        return { label: 'None', color: 'default', description: 'No Void' };
      default:
        return null;
    }
  };

  // Get accident display info
  const getAccidentInfo = (value) => {
    switch (value) {
      case 'S':
        return { label: 'Soiled', color: 'error', description: 'Soiled' };
      case 'W':
        return { label: 'Wet', color: 'warning', description: 'Wet' };
      case 'none':
        return { label: 'None', color: 'default', description: 'No Accident' };
      default:
        return null;
    }
  };

  // Get prompt display info
  const getPromptInfo = (value) => {
    const prompt = PROMPT_LEVELS.find((p) => p.value === value);
    const colorMap = { U: 'info', SI: 'success', P: 'default' };
    return prompt ? { label: prompt.description, color: colorMap[value] || 'default' } : null;
  };

  // Get button style for void
  const getVoidButtonStyle = (value, isSelected) => {
    let color = theme.palette.grey[600];
    let bgColor = 'transparent';
    let borderColor = theme.palette.grey[300];

    if (isSelected) {
      if (value === 'BM') {
        color = 'white';
        bgColor = theme.palette.success.main;
        borderColor = theme.palette.success.main;
      } else if (value === 'U') {
        color = 'white';
        bgColor = theme.palette.info.main;
        borderColor = theme.palette.info.main;
      } else if (value === 'N') {
        color = 'white';
        bgColor = theme.palette.grey[500];
        borderColor = theme.palette.grey[500];
      }
    }

    return {
      color,
      bgcolor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      fontWeight: isSelected ? 600 : 400,
      '&.MuiToggleButton-root': {
        border: `1px solid ${borderColor}`
      },
      '&:hover': {
        bgcolor: isSelected ? bgColor : alpha(theme.palette.grey[500], 0.08)
      },
      '&.Mui-selected': {
        bgcolor: bgColor,
        color,
        border: `1px solid ${borderColor}`,
        '&:hover': {
          bgcolor: isSelected ? alpha(borderColor, 0.85) : bgColor
        }
      }
    };
  };

  // Get button style for accident
  const getAccidentButtonStyle = (value, isSelected) => {
    let color = theme.palette.grey[600];
    let bgColor = 'transparent';
    let borderColor = theme.palette.grey[300];

    if (isSelected) {
      if (value === 'S') {
        color = 'white';
        bgColor = theme.palette.error.main;
        borderColor = theme.palette.error.main;
      } else if (value === 'W') {
        color = 'white';
        bgColor = theme.palette.warning.main;
        borderColor = theme.palette.warning.main;
      } else if (value === 'none') {
        color = 'white';
        bgColor = theme.palette.grey[500];
        borderColor = theme.palette.grey[500];
      }
    }

    return {
      color,
      bgcolor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      fontWeight: isSelected ? 600 : 400,
      '&.MuiToggleButton-root': {
        border: `1px solid ${borderColor}`
      },
      '&:hover': {
        bgcolor: isSelected ? bgColor : alpha(theme.palette.grey[500], 0.08)
      },
      '&.Mui-selected': {
        bgcolor: bgColor,
        color,
        border: `1px solid ${borderColor}`,
        '&:hover': {
          bgcolor: isSelected ? alpha(borderColor, 0.85) : bgColor
        }
      }
    };
  };

  // Render the add/edit form
  const renderForm = () => (
    <Paper sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {editingRecord ? intl.formatMessage({ id: 'form.toileting.editRecord' }) : intl.formatMessage({ id: 'form.toileting.newRecord' })}
      </Typography>

      <Stack spacing={3}>
        {/* Date and Time Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <DatePicker
            label={intl.formatMessage({ id: 'form.label.date' })}
            value={newRecord.date}
            onChange={(newValue) => setNewRecord({ ...newRecord, date: newValue })}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
          <TimePicker
            label={intl.formatMessage({ id: 'form.label.time' })}
            value={newRecord.time}
            onChange={(newValue) => setNewRecord({ ...newRecord, time: newValue })}
            timeSteps={{ minutes: 1 }}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Stack>

        {/* Prompt, Toilet Voids, and Accident - Same Row */}
        <Grid container spacing={2}>
          {/* Prompt */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.secondary.main, 0.03),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
                height: '100%'
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="secondary.main"
                sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {intl.formatMessage({ id: 'form.toileting.promptType' })}
              </Typography>
              <ToggleButtonGroup
                value={newRecord.prompt}
                exclusive
                onChange={(e, value) => setNewRecord({ ...newRecord, prompt: value || '' })}
                size="small"
              >
                <ToggleButton
                  value="P"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    minWidth: 32,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      bgcolor: 'secondary.main',
                      color: 'white',
                      borderColor: 'secondary.main',
                      '&:hover': { bgcolor: 'secondary.dark' }
                    }
                  }}
                >
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.prompted' })}>
                    <span>P</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value="U"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    minWidth: 32,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      bgcolor: 'info.main',
                      color: 'white',
                      borderColor: 'info.main',
                      '&:hover': { bgcolor: 'info.dark' }
                    }
                  }}
                >
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.unprompted' })}>
                    <span>U</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value="SI"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    minWidth: 32,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      bgcolor: 'success.main',
                      color: 'white',
                      borderColor: 'success.main',
                      '&:hover': { bgcolor: 'success.dark' }
                    }
                  }}
                >
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.selfInitiated' })}>
                    <span>SI</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Grid>

          {/* Toilet Voids */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.03),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`,
                height: '100%'
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="success.main"
                sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {intl.formatMessage({ id: 'form.toileting.toiletVoids' })}
              </Typography>
              <ToggleButtonGroup
                value={newRecord.toiletVoid}
                exclusive
                onChange={(e, value) => value !== null && setNewRecord({ ...newRecord, toiletVoid: value })}
                size="small"
              >
                <ToggleButton
                  value="BM"
                  sx={{ ...getVoidButtonStyle('BM', newRecord.toiletVoid === 'BM'), px: 1.5, minWidth: 40, py: 0.5 }}
                >
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.bowelMovement' })}>
                    <span>BM</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="U" sx={{ ...getVoidButtonStyle('U', newRecord.toiletVoid === 'U'), px: 1.5, minWidth: 32, py: 0.5 }}>
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.urine' })}>
                    <span>U</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="N" sx={{ ...getVoidButtonStyle('N', newRecord.toiletVoid === 'N'), px: 1.5, minWidth: 32, py: 0.5 }}>
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.noVoid' })}>
                    <span>N</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Grid>

          {/* Accident */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.03),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`,
                height: '100%'
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="error.main"
                sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {intl.formatMessage({ id: 'form.toileting.accident' })}
              </Typography>
              <ToggleButtonGroup
                value={newRecord.accident}
                exclusive
                onChange={(e, value) => setNewRecord({ ...newRecord, accident: value })}
                size="small"
              >
                <ToggleButton value="S" sx={{ ...getAccidentButtonStyle('S', newRecord.accident === 'S'), px: 1.5, minWidth: 32, py: 0.5 }}>
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.soiled' })}>
                    <span>S</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="W" sx={{ ...getAccidentButtonStyle('W', newRecord.accident === 'W'), px: 1.5, minWidth: 32, py: 0.5 }}>
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.wet' })}>
                    <span>W</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value="none"
                  sx={{ ...getAccidentButtonStyle('none', newRecord.accident === 'none'), px: 1.5, minWidth: 32, py: 0.5 }}
                >
                  <Tooltip title={intl.formatMessage({ id: 'form.toileting.none' })}>
                    <span>—</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Grid>
        </Grid>

        {/* Comments */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label={intl.formatMessage({ id: 'form.toileting.comments' })}
          placeholder={intl.formatMessage({ id: 'form.toileting.commentsPlaceholder' })}
          value={newRecord.comments}
          onChange={(e) => setNewRecord({ ...newRecord, comments: e.target.value })}
        />

        {/* Quick Reference - Bottom */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {intl.formatMessage({ id: 'form.toileting.quickReference' })}
          </Typography>
          <Grid container spacing={2}>
            {/* Prompt */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.secondary.main, 0.03),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`
                }}
              >
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ minWidth: 'fit-content' }}>
                    {intl.formatMessage({ id: 'form.toileting.promptLabel' })}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'secondary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      P
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.prompted' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'info.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      U
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.unprompted' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'success.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.6rem',
                        fontWeight: 700
                      }}
                    >
                      SI
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.selfInitiated' })}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Toilet Voids */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`
                }}
              >
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" fontWeight={700} color="success.main" sx={{ minWidth: 'fit-content' }}>
                    {intl.formatMessage({ id: 'form.toileting.voidsLabel' })}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'success.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 700
                      }}
                    >
                      BM
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.bowel' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'info.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      U
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.urine' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'grey.500',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      N
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.none' })}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Accident */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha(theme.palette.error.main, 0.03),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`
                }}
              >
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="caption" fontWeight={700} color="error.main" sx={{ minWidth: 'fit-content' }}>
                    {intl.formatMessage({ id: 'form.toileting.accidentLabel' })}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'error.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      S
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.soiled' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'warning.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      W
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.wet' })}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: 'grey.500',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 700
                      }}
                    >
                      -
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.toileting.none' })}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button variant="outlined" onClick={handleCancelForm}>
            {intl.formatMessage({ id: 'common.cancel' })}
          </Button>
          {editingRecord && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={() => {
                handleDeleteRecord(editingRecord.id);
                handleCancelForm();
              }}
            >
              {intl.formatMessage({ id: 'common.delete' })}
            </Button>
          )}
          <Button variant="contained" startIcon={<CheckOutlined />} onClick={handleSaveRecord}>
            {editingRecord ? intl.formatMessage({ id: 'form.toileting.update' }) : intl.formatMessage({ id: 'form.toileting.save' })}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  // Render a single record card (for individual view)
  const renderRecordCard = (record) => {
    const voidInfo = getVoidInfo(record.toiletVoid);
    const accidentInfo = getAccidentInfo(record.accident);
    const promptInfo = getPromptInfo(record.prompt);

    // Determine card accent color based on record status
    const hasAccident = record.accident === 'S' || record.accident === 'W';
    const isSuccess = record.toiletVoid === 'BM' || record.toiletVoid === 'U';
    const accentColor = hasAccident ? 'warning' : isSuccess ? 'success' : 'primary';

    return (
      <Paper
        key={record.id}
        elevation={0}
        sx={{
          p: 2.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          borderLeft: 4,
          borderLeftColor: `${accentColor}.main`,
          '&:hover': {
            boxShadow: theme.shadows[2]
          }
        }}
      >
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette[accentColor].main, 0.12)
                }}
              >
                <ClockCircleOutlined style={{ fontSize: '1.1rem', color: theme.palette[accentColor].main }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {record.date ? format(record.date, 'MMM dd, yyyy') : ''} - {record.time ? format(record.time, 'hh:mm a') : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" color="primary" onClick={() => handleEditRecord(record)}>
                <EditOutlined />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDeleteRecord(record.id)}>
                <DeleteOutlined />
              </IconButton>
            </Stack>
          </Stack>

          {/* Values in colored boxes */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {voidInfo && (
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor:
                    voidInfo.color === 'default' ? alpha(theme.palette.grey[500], 0.08) : alpha(theme.palette[voidInfo.color].main, 0.1),
                  borderRadius: 1.5,
                  border: `1px solid ${
                    voidInfo.color === 'default' ? alpha(theme.palette.grey[500], 0.2) : alpha(theme.palette[voidInfo.color].main, 0.2)
                  }`
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={voidInfo.color === 'default' ? 'text.secondary' : `${voidInfo.color}.main`}
                  sx={{ display: 'block', mb: 0.25 }}
                >
                  {intl.formatMessage({ id: 'form.toileting.toiletVoidSection' })}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {voidInfo.label}
                </Typography>
              </Paper>
            )}
            {accidentInfo && (
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor:
                    accidentInfo.color === 'default'
                      ? alpha(theme.palette.grey[500], 0.08)
                      : alpha(theme.palette[accidentInfo.color].main, 0.1),
                  borderRadius: 1.5,
                  border: `1px solid ${
                    accidentInfo.color === 'default'
                      ? alpha(theme.palette.grey[500], 0.2)
                      : alpha(theme.palette[accidentInfo.color].main, 0.2)
                  }`
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={accidentInfo.color === 'default' ? 'text.secondary' : `${accidentInfo.color}.main`}
                  sx={{ display: 'block', mb: 0.25 }}
                >
                  {intl.formatMessage({ id: 'form.toileting.accidentSection' })}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {accidentInfo.label}
                </Typography>
              </Paper>
            )}
            {promptInfo && (
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                }}
              >
                <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'block', mb: 0.25 }}>
                  {intl.formatMessage({ id: 'form.toileting.promptSection' })}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {promptInfo.label}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Comments */}
          {record.comments && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`
              }}
            >
              <Typography variant="caption" fontWeight={700} color="info.main" sx={{ mb: 0.5, display: 'block' }}>
                {intl.formatMessage({ id: 'form.toileting.commentsSection' })}
              </Typography>
              <Typography variant="body2" color="text.primary">
                {record.comments}
              </Typography>
            </Paper>
          )}
        </Stack>
      </Paper>
    );
  };

  // Render legend section
  const renderLegend = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
        {intl.formatMessage({ id: 'form.toileting.legendTitle' })}
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            {intl.formatMessage({ id: 'form.toileting.toiletVoidsLegend' })}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={intl.formatMessage({ id: 'form.toileting.bmEquals' })} size="small" variant="outlined" color="success" />
            <Chip label={intl.formatMessage({ id: 'form.toileting.uEquals' })} size="small" variant="outlined" color="info" />
            <Chip label={intl.formatMessage({ id: 'form.toileting.nEquals' })} size="small" variant="outlined" />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            {intl.formatMessage({ id: 'form.toileting.accidentsLegend' })}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={intl.formatMessage({ id: 'form.toileting.sEquals' })} size="small" variant="outlined" color="error" />
            <Chip label={intl.formatMessage({ id: 'form.toileting.wEquals' })} size="small" variant="outlined" color="warning" />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            {intl.formatMessage({ id: 'form.toileting.promptsLegend' })}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PROMPT_LEVELS.map((p) => (
              <Tooltip key={p.value} title={p.description}>
                <Chip label={`${p.value} = ${p.description}`} size="small" variant="outlined" />
              </Tooltip>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  // INDIVIDUAL VIEW
  if (viewMode === 'individual') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack spacing={3}>
          <MainCard>
            <Stack spacing={2}>
              {/* Header with New Button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">{intl.formatMessage({ id: 'form.toileting.records' }, { count: records.length })}</Typography>
                {!showAddForm && (
                  <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>
                    {intl.formatMessage({ id: 'form.toileting.new' })}
                  </Button>
                )}
              </Stack>

              {/* Add/Edit Form */}
              {showAddForm && renderForm()}

              {/* Records List */}
              {!showAddForm && records.length === 0 ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                  <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
                  <Typography variant="h6" color="text.secondary">
                    {intl.formatMessage({ id: 'form.toileting.noRecordsYet' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: 'form.toileting.clickNew' })}
                  </Typography>
                </Stack>
              ) : !showAddForm ? (
                <Stack spacing={2}>{records.map((record) => renderRecordCard(record))}</Stack>
              ) : null}
            </Stack>
          </MainCard>

          {/* Legend Section */}
          {renderLegend()}
        </Stack>
      </LocalizationProvider>
    );
  }

  // Render grid form view (Type 1)
  const renderGridFormView = () => (
    <MainCard
      title={
        editingRecord
          ? intl.formatMessage({ id: 'form.toileting.editToiletingRecord' })
          : intl.formatMessage({ id: 'form.toileting.newToiletingRecord' })
      }
      secondary={
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            {intl.formatMessage({ id: 'form.button.back' })}
          </Button>
          <Button variant="contained" color="primary" startIcon={<CheckOutlined />} onClick={handleSaveAndBackToList}>
            {intl.formatMessage({ id: 'form.toileting.save' })}
          </Button>
        </Stack>
      }
    >
      {renderForm()}
    </MainCard>
  );

  // Render inline edit row for Type 1
  const renderInlineEditRowType1 = () => (
    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
      <TableCell>
        <Stack spacing={1}>
          <DatePicker
            value={inlineDate}
            onChange={(newValue) => setInlineDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 140 }
              }
            }}
          />
          <TimePicker
            value={inlineTime}
            onChange={(newValue) => setInlineTime(newValue)}
            timeSteps={{ minutes: 1 }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 140 }
              }
            }}
          />
        </Stack>
      </TableCell>
      <TableCell align="center">
        <ToggleButtonGroup value={inlineToiletVoid} exclusive onChange={(e, value) => setInlineToiletVoid(value)} size="small">
          <ToggleButton value="BM" sx={{ ...getVoidButtonStyle('BM', inlineToiletVoid === 'BM'), px: 1.5, minWidth: 40, py: 0.5 }}>
            BM
          </ToggleButton>
          <ToggleButton value="U" sx={{ ...getVoidButtonStyle('U', inlineToiletVoid === 'U'), px: 1.5, minWidth: 32, py: 0.5 }}>
            U
          </ToggleButton>
          <ToggleButton value="N" sx={{ ...getVoidButtonStyle('N', inlineToiletVoid === 'N'), px: 1.5, minWidth: 32, py: 0.5 }}>
            N
          </ToggleButton>
        </ToggleButtonGroup>
      </TableCell>
      <TableCell align="center">
        <ToggleButtonGroup value={inlineAccident} exclusive onChange={(e, value) => setInlineAccident(value)} size="small">
          <ToggleButton value="S" sx={{ ...getAccidentButtonStyle('S', inlineAccident === 'S'), px: 1.5, minWidth: 32, py: 0.5 }}>
            S
          </ToggleButton>
          <ToggleButton value="W" sx={{ ...getAccidentButtonStyle('W', inlineAccident === 'W'), px: 1.5, minWidth: 32, py: 0.5 }}>
            W
          </ToggleButton>
          <ToggleButton value="none" sx={{ ...getAccidentButtonStyle('none', inlineAccident === 'none'), px: 1.5, minWidth: 32, py: 0.5 }}>
            —
          </ToggleButton>
        </ToggleButtonGroup>
      </TableCell>
      <TableCell align="center">
        <ToggleButtonGroup value={inlinePrompt} exclusive onChange={(e, value) => setInlinePrompt(value || '')} size="small">
          <ToggleButton
            value="P"
            sx={{
              px: 1.5,
              minWidth: 32,
              py: 0.5,
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: '8px',
              '&.Mui-selected': {
                bgcolor: 'secondary.main',
                color: 'white',
                borderColor: 'secondary.main',
                '&:hover': { bgcolor: 'secondary.dark' }
              }
            }}
          >
            P
          </ToggleButton>
          <ToggleButton
            value="U"
            sx={{
              px: 1.5,
              minWidth: 32,
              py: 0.5,
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: '8px',
              '&.Mui-selected': { bgcolor: 'info.main', color: 'white', borderColor: 'info.main', '&:hover': { bgcolor: 'info.dark' } }
            }}
          >
            U
          </ToggleButton>
          <ToggleButton
            value="SI"
            sx={{
              px: 1.5,
              minWidth: 32,
              py: 0.5,
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: '8px',
              '&.Mui-selected': {
                bgcolor: 'success.main',
                color: 'white',
                borderColor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              }
            }}
          >
            SI
          </ToggleButton>
        </ToggleButtonGroup>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          placeholder={intl.formatMessage({ id: 'form.toileting.commentsShort' })}
          value={inlineComments}
          onChange={(e) => setInlineComments(e.target.value)}
          sx={{ minWidth: 150 }}
        />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title={intl.formatMessage({ id: 'common.save' })}>
            <IconButton
              size="small"
              color="success"
              onClick={handleSaveInlineRecord}
              disabled={!inlineToiletVoid && !inlineAccident && !inlinePrompt}
            >
              <CheckOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: 'common.cancel' })}>
            <IconButton size="small" color="error" onClick={handleCancelInline}>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );

  // Render Type 1 data entry table
  const renderType1DataEntryTable = (recordsList, showActions = true) => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{intl.formatMessage({ id: 'form.toileting.dateTime' })}</TableCell>
            <TableCell align="center">{intl.formatMessage({ id: 'form.toileting.toiletVoid' })}</TableCell>
            <TableCell align="center">{intl.formatMessage({ id: 'form.toileting.accident' })}</TableCell>
            <TableCell align="center">{intl.formatMessage({ id: 'form.toileting.prompt' })}</TableCell>
            <TableCell>{intl.formatMessage({ id: 'form.toileting.comments' })}</TableCell>
            {showActions && <TableCell align="right">{intl.formatMessage({ id: 'form.toileting.actions' })}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Inline edit row for new records */}
          {showActions && inlineEditing && renderInlineEditRowType1()}
          {recordsList.length === 0 && !inlineEditing ? (
            <TableRow>
              <TableCell colSpan={showActions ? 6 : 5} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {showActions
                    ? intl.formatMessage({ id: 'form.toileting.noRecordsToday' })
                    : intl.formatMessage({ id: 'form.toileting.noRecordsFound' })}
                </Typography>
                {showActions && (
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.toileting.clickNewRecord' })}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ) : (
            recordsList.map((record) => {
              // Pre-computed display info — kept for upcoming status pills on
              // each row; ToggleButtonGroups below render the raw values today.
              /* eslint-disable no-unused-vars */
              const voidInfo = getVoidInfo(record.toiletVoid);
              const accidentInfo = getAccidentInfo(record.accident);
              const promptInfo = getPromptInfo(record.prompt);
              /* eslint-enable no-unused-vars */

              return (
                <TableRow
                  key={record.id}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {record.date ? format(new Date(record.date), 'MMM dd') : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {record.time ? format(new Date(record.time), 'hh:mm a') : '-'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <ToggleButtonGroup
                      value={record.toiletVoid}
                      exclusive
                      onChange={(e, value) => value !== null && handleRecordChange(record.id, 'toiletVoid', value)}
                      size="small"
                    >
                      <ToggleButton
                        value="BM"
                        sx={{ ...getVoidButtonStyle('BM', record.toiletVoid === 'BM'), px: 1.5, minWidth: 40, py: 0.5 }}
                      >
                        BM
                      </ToggleButton>
                      <ToggleButton
                        value="U"
                        sx={{ ...getVoidButtonStyle('U', record.toiletVoid === 'U'), px: 1.5, minWidth: 32, py: 0.5 }}
                      >
                        U
                      </ToggleButton>
                      <ToggleButton
                        value="N"
                        sx={{ ...getVoidButtonStyle('N', record.toiletVoid === 'N'), px: 1.5, minWidth: 32, py: 0.5 }}
                      >
                        N
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                  <TableCell align="center">
                    <ToggleButtonGroup
                      value={record.accident}
                      exclusive
                      onChange={(e, value) => handleRecordChange(record.id, 'accident', value)}
                      size="small"
                    >
                      <ToggleButton
                        value="S"
                        sx={{ ...getAccidentButtonStyle('S', record.accident === 'S'), px: 1.5, minWidth: 32, py: 0.5 }}
                      >
                        S
                      </ToggleButton>
                      <ToggleButton
                        value="W"
                        sx={{ ...getAccidentButtonStyle('W', record.accident === 'W'), px: 1.5, minWidth: 32, py: 0.5 }}
                      >
                        W
                      </ToggleButton>
                      <ToggleButton
                        value="none"
                        sx={{ ...getAccidentButtonStyle('none', record.accident === 'none'), px: 1.5, minWidth: 32, py: 0.5 }}
                      >
                        —
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                  <TableCell align="center">
                    <ToggleButtonGroup
                      value={record.prompt}
                      exclusive
                      onChange={(e, value) => handleRecordChange(record.id, 'prompt', value || '')}
                      size="small"
                    >
                      <ToggleButton
                        value="P"
                        sx={{
                          px: 1.5,
                          minWidth: 32,
                          py: 0.5,
                          border: `1px solid ${theme.palette.grey[300]}`,
                          borderRadius: '8px',
                          '&.Mui-selected': {
                            bgcolor: 'secondary.main',
                            color: 'white',
                            borderColor: 'secondary.main',
                            '&:hover': { bgcolor: 'secondary.dark' }
                          }
                        }}
                      >
                        P
                      </ToggleButton>
                      <ToggleButton
                        value="U"
                        sx={{
                          px: 1.5,
                          minWidth: 32,
                          py: 0.5,
                          border: `1px solid ${theme.palette.grey[300]}`,
                          borderRadius: '8px',
                          '&.Mui-selected': {
                            bgcolor: 'info.main',
                            color: 'white',
                            borderColor: 'info.main',
                            '&:hover': { bgcolor: 'info.dark' }
                          }
                        }}
                      >
                        U
                      </ToggleButton>
                      <ToggleButton
                        value="SI"
                        sx={{
                          px: 1.5,
                          minWidth: 32,
                          py: 0.5,
                          border: `1px solid ${theme.palette.grey[300]}`,
                          borderRadius: '8px',
                          '&.Mui-selected': {
                            bgcolor: 'success.main',
                            color: 'white',
                            borderColor: 'success.main',
                            '&:hover': { bgcolor: 'success.dark' }
                          }
                        }}
                      >
                        SI
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }} noWrap>
                      {record.comments || '-'}
                    </Typography>
                  </TableCell>
                  {showActions && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title={intl.formatMessage({ id: 'form.toileting.editRecord' })}>
                          <IconButton size="small" color="primary" onClick={() => handleEditRecordGridView(record)}>
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({ id: 'form.toileting.deleteRecord' })}>
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(record)}>
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // GRID VIEW - when in form mode, show the form
  if (internalFormMode === 'form') {
    return <LocalizationProvider dateAdapter={AdapterDateFns}>{renderGridFormView()}</LocalizationProvider>;
  }

  // GRID VIEW with tabs - Data Entry and Historical Data
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MainCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
            <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
          </Tabs>
          <Stack direction="row" spacing={1} sx={{ pr: 2 }}>
            {tabValue === 0 && (
              <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleNewRecord} disabled={inlineEditing}>
                {intl.formatMessage({ id: 'form.toileting.newRecord' })}
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ pt: 3 }}>
          {/* Tab 0: Data Entry - Today's Records with Add/Edit/Delete */}
          {tabValue === 0 && renderType1DataEntryTable(todaysRecords, true)}

          {/* Tab 1: Historical Data - All Records (Read-only view) */}
          {tabValue === 1 &&
            (historyIsEmpty && allRecords.length === 0 ? (
              <HistoryEmptyState />
            ) : (
              <>
                {renderType1DataEntryTable(allRecords, false)}
                <HistoryPagination
                  page={historyPage}
                  totalPages={historyTotalPages}
                  total={historyTotal}
                  pageSize={HISTORY_PAGE_SIZE}
                  onChange={setHistoryPage}
                  loading={historyLoading}
                />
              </>
            ))}
        </Box>
      </MainCard>

      {/* Legend Section */}
      <Box sx={{ mt: 3 }}>{renderLegend()}</Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>{intl.formatMessage({ id: 'form.toileting.deleteDialogTitle' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{intl.formatMessage({ id: 'form.toileting.deleteDialogMessage' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {intl.formatMessage({ id: 'common.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

ToiletingProgramMode.propTypes = {
  onDataChange: PropTypes.func,
  viewMode: PropTypes.oneOf(['individual', 'grid']),
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string
};

export default ToiletingProgramMode;
